// ── SourceScopesPanel — Left panel: tenants, namespaces, scope keys ────────
import React from 'react';
import { Check, Minus, Eye, ArrowRightLeft } from 'lucide-react';
import NamespaceList, {
  SearchInput,
} from '@/shared/components/ui/NamespaceList';
import { timeAgo } from '@/utils/date';
import type {
  WorkflowScope,
  ScopeNamespaceCount,
  Tenant,
  ScopeManifest,
  MigrationStep,
} from '../types';
import { _ns, _sk, decodeScopeKey, scopeKeyDisplay } from '../utils';

interface SourceScopesPanelProps {
  // Tenant context (read-only — driven by sidebar TenantSelector)
  activeTenantId: number | '';
  tenantList: Tenant[];

  // Namespace sidebar
  namespaces: ScopeNamespaceCount[];
  selectedNs: string | null;
  onSelectNs: (ns: string | null) => void;
  nsSelectionState: Map<string, 'all' | 'partial' | 'none'>;
  onToggleNamespaceScopes: (ns: string) => Promise<void>;
  allNsSelectState: 'all' | 'partial' | 'none';
  onToggleAllNamespaces: () => Promise<void>;
  loadingNs: Set<string>;
  nsSearch: string;
  onNsSearchChange: (v: string) => void;
  nsLoading: boolean;

  // Scope keys
  keySearch: string;
  onKeySearchChange: (v: string) => void;
  filteredScopes: WorkflowScope[];
  selectedScopeIds: Set<number>;
  onToggleScopeId: (id: number) => void;
  onToggleAllScopes: () => void;
  allFilteredSelected: boolean;
  someFilteredSelected: boolean;
  keysLoading: boolean;

  // Header stats
  selectedCount: number;
  totalScopeCount: number;
  namespacesRawCount: number;
  onClearSelection: () => void;

  // Action bar
  exportStep: MigrationStep;
  onSetExportStep: (s: MigrationStep) => void;
  canExport: boolean;
  exportPending: boolean;
  onExport: () => void;
  manifest: ScopeManifest | null;
  onShowExportSummary: () => void;
}

export function SourceScopesPanel({
  activeTenantId,
  tenantList,
  namespaces,
  selectedNs,
  onSelectNs,
  nsSelectionState,
  onToggleNamespaceScopes,
  allNsSelectState,
  onToggleAllNamespaces,
  loadingNs,
  nsSearch,
  onNsSearchChange,
  nsLoading,
  keySearch,
  onKeySearchChange,
  filteredScopes,
  selectedScopeIds,
  onToggleScopeId,
  onToggleAllScopes,
  allFilteredSelected,
  someFilteredSelected,
  keysLoading,
  selectedCount,
  totalScopeCount,
  namespacesRawCount,
  onClearSelection,
  exportStep,
  onSetExportStep,
  canExport,
  exportPending,
  onExport,
  manifest,
  onShowExportSummary,
}: SourceScopesPanelProps) {
  return (
    <>
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="px-3 py-2 border-b border-neutral-800 pac-glass-subtle flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-neutral-200">
            Source Scopes
          </span>
          {activeTenantId !== '' && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-900/40 text-indigo-300 border border-indigo-700/40">
              🏢 {tenantList.find(t => t.idTenant === activeTenantId)?.tenantName ?? `#${activeTenantId}`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <>
              <span className="text-[10px] text-emerald-400 tabular-nums">
                {selectedCount} / {totalScopeCount.toLocaleString()} selected
              </span>
              <button
                type="button"
                onClick={onClearSelection}
                className="text-[10px] text-neutral-500 hover:text-rose-400 transition-colors"
              >
                Clear
              </button>
              <span className="text-neutral-700">·</span>
            </>
          )}
          <span className="text-[10px] text-neutral-500">
            {namespacesRawCount} ns
          </span>
        </div>
      </div>

      {/* ── Grid: namespace | scope keys ───────────── */}
      <div className="flex-1 min-h-0 grid grid-cols-[180px_1fr] overflow-hidden">
        {/* ── Namespace sidebar ── */}
        <NamespaceList
          namespaces={namespaces}
          selectedNamespace={selectedNs}
          onSelect={(ns) => {
            onSelectNs(ns);
            onKeySearchChange('');
          }}
          selectionState={nsSelectionState}
          onToggleSelection={onToggleNamespaceScopes}
          selectAllState={allNsSelectState}
          onSelectAll={onToggleAllNamespaces}
          loadingNamespaces={loadingNs}
          searchValue={nsSearch}
          onSearchChange={onNsSearchChange}
          loading={nsLoading}
        />

        {/* ── Scope keys list ── */}
        <div className="min-h-0 flex flex-col overflow-hidden">
          {selectedNs && (
            <div className="px-2 py-1.5 border-b border-neutral-800/50 flex items-center gap-2">
              <button
                type="button"
                onClick={onToggleAllScopes}
                className="shrink-0 group"
                title={
                  allFilteredSelected
                    ? 'Deselect all'
                    : 'Select all'
                }
              >
                {allFilteredSelected ? (
                  <span className="w-3.5 h-3.5 rounded-[3px] flex items-center justify-center bg-emerald-600 border border-emerald-500 group-hover:bg-emerald-500 transition-all">
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  </span>
                ) : someFilteredSelected ? (
                  <span className="w-3.5 h-3.5 rounded-[3px] flex items-center justify-center bg-amber-600/80 border border-amber-500 group-hover:bg-amber-500 transition-all">
                    <Minus className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  </span>
                ) : (
                  <span className="w-3.5 h-3.5 rounded-[3px] inline-block border border-neutral-600 group-hover:border-neutral-400 transition-colors" />
                )}
              </button>
              <SearchInput
                value={keySearch}
                onChange={onKeySearchChange}
                placeholder="Filter keys…"
              />
              <span className="text-[10px] text-neutral-500 shrink-0 tabular-nums">
                {selectedCount > 0 && (
                  <span className="text-emerald-400 mr-1">
                    {
                      filteredScopes.filter(
                        (s) =>
                          s.id_scope != null &&
                          selectedScopeIds.has(s.id_scope),
                      ).length
                    }
                    /
                  </span>
                )}
                {filteredScopes.length}
              </span>
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {!selectedNs ? (
              <div className="h-full flex items-center justify-center">
                <span className="text-[11px] text-neutral-500">
                  Select a namespace
                </span>
              </div>
            ) : keysLoading ? (
              <div className="h-full flex items-center justify-center">
                <span className="text-[11px] text-neutral-400">Loading…</span>
              </div>
            ) : filteredScopes.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <span className="text-[11px] text-neutral-500">
                  {keySearch ? 'No matching keys.' : 'Empty namespace.'}
                </span>
              </div>
            ) : (
              filteredScopes.map((s, idx) => {
                const id = s.id_scope;
                const isSelected =
                  id != null && selectedScopeIds.has(id);
                const raw = _sk(s);
                const decoded = decodeScopeKey(raw);
                const display = scopeKeyDisplay(s);

                return (
                  <button
                    key={`${_ns(s)}::${raw}::${idx}`}
                    type="button"
                    onClick={() => id != null && onToggleScopeId(id)}
                    className={
                      'w-full text-left px-2 py-1.5 text-[11px] border-b border-neutral-800/30 ' +
                      'flex items-center gap-2 transition-colors cursor-default group ' +
                      (isSelected
                        ? 'bg-emerald-950/40 text-emerald-200 border-l-2 border-l-emerald-500'
                        : 'text-neutral-300 hover:bg-black')
                    }
                  >
                    <span className="shrink-0">
                      {isSelected ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <span className="w-3.5 h-3.5 inline-block border border-neutral-600 rounded-[3px] group-hover:border-neutral-400 transition-colors" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono" title={raw}>
                        {display}
                      </div>
                      {decoded && (
                        <div className="text-[9px] text-neutral-500 truncate">
                          {decoded.label}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right space-y-px">
                      {/* ── Record counts (hot + archived) ── */}
                      {((s.hot_stream_count ?? 0) > 0 || (s.rows_archived ?? 0) > 0) && (
                        <div
                          className="text-[8px] tabular-nums text-cyan-500/80"
                          title={`Hot: ${(s.hot_stream_count ?? 0).toLocaleString()} rows · Archived: ${(s.rows_archived ?? 0).toLocaleString()} rows`}
                        >
                          🔥{(s.hot_stream_count ?? 0).toLocaleString()}
                          {(s.rows_archived ?? 0) > 0 && (
                            <span className="text-neutral-600 ml-0.5">+{(s.rows_archived ?? 0).toLocaleString()}ar</span>
                          )}
                        </div>
                      )}
                      <div
                        className="text-[9px] text-neutral-600"
                        title={s.created_at ? `Created: ${new Date(s.created_at).toLocaleString()}` : undefined}
                      >
                        {s.created_at ? timeAgo(s.created_at) : ''}
                      </div>
                      {s.last_hot_stream_at && (
                        <div
                          className="text-[8px] text-amber-600/80"
                          title={`Last hot-stream: ${new Date(s.last_hot_stream_at).toLocaleString()}`}
                        >
                          ⚡ {timeAgo(s.last_hot_stream_at)}
                        </div>
                      )}
                      {id != null && (
                        <div className="text-[8px] text-neutral-700 tabular-nums">
                          id:{id}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* ── Export action bar ── */}
      <div className="px-3 py-2 border-t border-neutral-800 pac-glass flex items-center justify-between gap-2 shrink-0">
        <div className="text-[10px] text-neutral-500 truncate">
          {selectedCount > 0 ? (
            <>
              Selected:{' '}
              <span className="text-neutral-300">
                {selectedCount} scope{selectedCount !== 1 ? 's' : ''}
              </span>
            </>
          ) : (
            'Check scopes to select for export'
          )}
        </div>
        {exportStep === 'select' ? (
          <button
            className={
              'px-3 py-1.5 rounded border text-xs flex items-center gap-1.5 ' +
              (selectedCount > 0
                ? 'border-sky-700 text-sky-200 hover:border-sky-500'
                : 'border-neutral-800 text-neutral-600 cursor-not-allowed')
            }
            disabled={selectedCount === 0}
            onClick={() => onSetExportStep('preview')}
          >
            <Eye className="w-3 h-3" />
            Preview
          </button>
        ) : exportStep === 'preview' ? (
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 rounded border border-neutral-700 text-xs text-neutral-300 hover:border-neutral-500"
              onClick={() => onSetExportStep('select')}
            >
              ← Back
            </button>
            <button
              className={
                'px-3 py-1.5 rounded border text-xs flex items-center gap-1.5 ' +
                (canExport
                  ? 'border-emerald-700 text-emerald-200 hover:border-emerald-500'
                  : 'border-neutral-800 text-neutral-600 cursor-not-allowed')
              }
              disabled={!canExport}
              onClick={onExport}
            >
              <ArrowRightLeft className="w-3 h-3" />
              {exportPending ? 'Exporting…' : 'Confirm Export'}
            </button>
          </div>
        ) : (
          <button
            className="px-3 py-1.5 rounded border border-neutral-700 text-xs text-neutral-300 hover:border-neutral-500"
            onClick={() => {
              if (manifest) {
                onShowExportSummary();
              } else {
                onSetExportStep('select');
                onClearSelection();
              }
            }}
          >
            New Export
          </button>
        )}
      </div>
    </>
  );
}
